<template>
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mx-4 sm:mx-12">
    <div class="flex items-center justify-between gap-x-4 w-full pb-2">
      <span class="font-semibold text-blue-600 text-2xl shrink-0">Order Fulfillment</span>
      <transition
        name="quick-fade"
        mode="out-in"
      >
        <BatchModifierControls
          v-if="currentBatchAction"
          :label="currentBatchAction.label"
          :disabled="submittingBatch || !currentBatchAction.canSubmit"
          :submitting="submittingBatch"
          @cancel="batchActionStop"
          @submit="batchActionSubmit"
        />
        <IndexActionMenu v-else>
          <MenuItem
            as="button"
            class="text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-3 text-sm w-full"
            v-for="action in batchActions"
            @click="batchActionStart(action)"
          >
            {{ action.label }}
          </MenuItem>
          <MenuItem>
            <button
              type="button"
              @click="isSoundMuted ? enableSound() : disableSound()"
              class="text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center justify-between px-4 py-3 text-sm w-full"
            >
              <span><template v-if="isSoundMuted">Unmute Sound</template><template v-else>Mute Sound</template></span>
              <SpeakerXMarkIcon
                v-if="isSoundMuted"
                class="h-5 w-5 text-gray-600"
              />
              <SpeakerWaveIcon
                v-else
                class="h-5 w-5 text-gray-600"
              />
            </button>
          </MenuItem>
        </IndexActionMenu>
      </transition>
    </div>
  </div>

  <div class="w-full flex flex-col h-full overflow-y-auto">
    <div class="flex justify-between py-2 px-4 sm:px-12">
      <div class="w-full md:w-64">
        <LocationsMenu
          :locations="page.props.auth.locations.data"
          @selectLocation="selectedLocationId = $event.id"
          :selected-location-id="selectedLocationId"
        />
      </div>
    </div>
    <AgGridVue
      v-bind="gridOptions"
      class="ag-theme-alpine salve-theme-fulfillment"
    />
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted, watch, onMounted } from 'vue'
import FulfillmentCell from '@/Pages/Fulfillment/DataGrid/FulfillmentCell.vue'
import { AgGridVue } from 'ag-grid-vue3'
import { FULFILLMENT_STATUS } from '@/Composables/Fulfillment/constants'
import { useAccessCompany } from '@/Composables/access-company'
import { useEcho, createChannel, ECHO_EVENTS, PUSHER_CONNECTION_STATES } from '@/echo'
import { router } from '@inertiajs/vue3'
import { settle } from '@/utils/promises'
import { api } from '@/Composables/Fulfillment/fulfillment-service-api'
import { usePoll } from '@/Composables/poll'
import { SYNC_ORDERS_TABLE_MS, SYNC_ORDERS_TABLE_MS_FAST } from '@/Composables/Fulfillment/constants'
import { DateTime } from 'luxon'
import OrderWithCustomer from '@/Pages/Fulfillment/DataGrid/OrderWithCustomerCell.vue'
import OrderWithCustomerHeader from '@/Pages/Fulfillment/DataGrid/OrderWithCustomerHeader.vue'
import DeliveryCell from '@/Components/DataGrid/Cells/DeliveryCell.vue'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import { useMobileWidths } from '@/Components/DataGrid/Composables/mobile-widths'
import { useAccessUser } from '@/Composables/access-user'
import { usePage } from '@inertiajs/vue3'
import { useLocalStorage } from '@vueuse/core'
import { useSound, SOUND_TYPE } from '@/Composables/sound'
import IndexActionMenu from '@/Components/IndexActionMenu.vue'
import LocationsMenu from '@/Components/DataGrid/Filters/LocationsMenu.vue'
import { useBatchModifier } from '@/Components/DataGrid/Composables/batch-modifiers'
import CheckmarkCell from '@/Components/DataGrid/Cells/CheckmarkCell.vue'
import { useErrors } from '@/Composables/errors'
import { MenuItem } from '@headlessui/vue'
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons-2/vue/24/outline'
import BatchModifierControls from '@/Components/BatchModifierControls.vue'
import { useGridApi } from '@/Components/DataGrid/grid-api'

const { gridApi, createGridOptions } = useGridApi()
const { echoClient: echo, pusherConnectionState } = useEcho()
const { company } = useAccessCompany()
const { showHardError } = useErrors()
const { currentLocation } = useAccessUser()
const page = usePage()

const tableInitialized = ref(false)

const FULFILLMENTS_CHANNEL = createChannel('fulfillments', company.value.id)

const selectedLocationId = useLocalStorage('fulfillment-table-location', currentLocation.value.id)

const { playSound, isSoundMuted, enableSound, disableSound } = useSound([SOUND_TYPE.UPDATE])

const playNotificationSound = () => {
  if (tableInitialized.value) {
    playSound(SOUND_TYPE.UPDATE)
  }
}

const isRowNewer = (incomingRow, currentRow) => {
  return new Date(incomingRow.updated_at) > new Date(currentRow.updated_at)
}

const hasChangeOfInterest = (incomingRow, currentRow) => {
  if (
    incomingRow.fulfillment_status === FULFILLMENT_STATUS.notBagged &&
    currentRow.fulfillment_status !== FULFILLMENT_STATUS.notBagged
  ) {
    return true
  }

  if (
    incomingRow.fulfillment_status === FULFILLMENT_STATUS.changed &&
    currentRow.fulfillment_status !== FULFILLMENT_STATUS.changed
  ) {
    return true
  }
}

const processRowUpdate = (incomingRowData, currentRowData) => {
  if (isRowNewer(incomingRowData, currentRowData)) {
    if (hasChangeOfInterest(incomingRowData, currentRowData)) {
      console.log('🔈 change of interest')
      playNotificationSound()
    }
    return true
  }
  return false
}

const updateTable = (data) => {
  try {
    const incomingData = new Map(data.rows.map((row) => [row.id, row]))

    const rowsToUpdate = []
    const rowsToRemove = []

    gridApi.value.forEachNode((node) => {
      const currentRowData = node.data
      const incomingRowData = incomingData.get(currentRowData.id)

      if (incomingRowData) {
        const shouldUpdate = processRowUpdate(incomingRowData, currentRowData)
        if (shouldUpdate) {
          rowsToUpdate.push(incomingRowData)
        }
        incomingData.delete(currentRowData.id)
      } else {
        rowsToRemove.push({ id: currentRowData.id })
      }
    })

    const rowsToAdd = Array.from(incomingData.values())

    if (rowsToAdd.length) {
      console.log('🔈 new rows')
      playNotificationSound()
    }

    gridApi.value.applyTransaction({
      update: rowsToUpdate,
      remove: rowsToRemove,
      add: rowsToAdd
    })

    tableInitialized.value = true
    console.log('table initialized')
  } catch (e) {
    console.error(e)
    showHardError()
  }
}

const patchRow = (incomingData) => {
  try {
    const currentRow = gridApi.value.getRowNode(incomingData.id)

    // @Note: It's possible to receive patch events for orders that are filtered out of the table.
    if (!currentRow) {
      console.info('Could not find row to patch')
      return
    }
    const newRowData = { ...currentRow.data, ...incomingData }
    const shouldUpdate = processRowUpdate(newRowData, currentRow.data)
    if (shouldUpdate) {
      currentRow.setData(newRowData)
    }
  } catch (e) {
    console.error(e)
    showHardError()
  }
}

const syncOrdersTable = async () => {
  console.log('syncing orders table')

  api.cancelFetchOrders()

  const [err, result] = await settle(api.fetchOrders({ locationId: selectedLocationId.value }))

  if (err) {
    if (err.name === 'AbortError') {
      return
    }
    console.error('Failed to fetch orders')
    await showHardError()
  }

  const [_, data] = result

  updateTable(data)
}

const onLockChanged = (data) => {
  console.info('onLockChanged', data)
  patchRow({ id: data.id, is_locked: data.is_locked, updated_at: data.updated_at })
}

const onFulfillmentUpdated = (data) => {
  console.info('onFulfillmentUpdated', data)
  patchRow(data)
}

// If the web socket connection is unavailable, poll the server more frequently.
const syncOrdersTableInterval = computed(() => {
  return pusherConnectionState.value === PUSHER_CONNECTION_STATES.CONNECTED
    ? SYNC_ORDERS_TABLE_MS
    : SYNC_ORDERS_TABLE_MS_FAST
})

const syncOrdersTablePoll = usePoll(syncOrdersTable, { interval: syncOrdersTableInterval })

// If the table data hasn't been fetched & instantiated, websocket patch events are queued until the table is initialized.
const wsEventQueue = new Set()
const enqueue = (cb) => {
  if (tableInitialized.value) {
    console.log('Table intialized, running callback')
    cb()
  } else {
    console.log('Table not intialized, adding to queue')
    wsEventQueue.add(cb)
  }
}

watch(
  gridApi,
  async () => {
    await syncOrdersTable()

    if (wsEventQueue.size > 0) {
      console.log('Running queued ws events.')
      wsEventQueue.forEach((cb) => cb())
      wsEventQueue.clear()
    }

    console.log('orders table synced')
    syncOrdersTablePoll.start()
  },
  { once: true }
)

const hardRefreshTable = async () => {
  syncOrdersTablePoll.stop()
  tableInitialized.value = false
  await syncOrdersTable()
  syncOrdersTablePoll.start()
}

watch(selectedLocationId, async () => {
  await hardRefreshTable()
})

onMounted(() => {
  // @Note: If the WS connection is unavailable on mount or later dropped,
  // the channel and event listeners will be correctly established / maintained by echo.
  const channel = echo.private(FULFILLMENTS_CHANNEL)
  channel.listen(ECHO_EVENTS.ORDER_LOCK_CHANGED, (data) => enqueue(() => onLockChanged(data)))
  channel.listen(ECHO_EVENTS.ORDER_FULFILLMENT_UPDATED, (data) => enqueue(() => onFulfillmentUpdated(data)))
})

onUnmounted(() => {
  echo.leave(FULFILLMENTS_CHANNEL)
})

const { currentBatchAction, submittingBatch, batchActionStart, batchActionStop, batchActionSubmit, onSubmitBatch } =
  useBatchModifier(gridApi)

onSubmitBatch(hardRefreshTable)

const batchActions = [
  {
    label: 'Cancel Orders',
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to cancel these orders?',
    actionFn: api.cancelOrders
  },
  {
    label: 'Notify Orders',
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to notify these orders?',
    actionFn: api.notifyOrders
  }
]

// Setup Grid Options
const gridOptions = createGridOptions({
  onGridReady: (params) => {
    gridApi.value = params.api
  },

  getRowId: (params) => params.data.id,

  columnDefs: [
    {
      field: 'priority',
      hide: true,
      sort: 'asc'
    },
    {
      headerName: '',
      field: 'checkbox',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      hide: true
    },
    {
      headerName: '#',
      field: 'id',
      filter: 'agTextColumnFilter',
      mobileWidth: 200,
      minWidth: 200,
      flex: 1,
      headerComponent: OrderWithCustomerHeader,
      cellRenderer: OrderWithCustomer,
      cellRendererParams: (params) => ({
        orderId: params.data.id,
        referenceNumber: params.data.reference_number,
        customerName: params.data.customer_name
      })
    },
    {
      headerName: 'Date',
      field: 'to_fulfill_at',
      filter: 'agDateColumnFilter',
      valueGetter: (params) => {
        return params.data.to_fulfill_at
          ? DateTime.fromISO(params.data.to_fulfill_at).startOf('day').toFormat('MM/dd/yyyy')
          : '--'
      },
      filterValueGetter: (params) => {
        return params.data.to_fulfill_at ? DateTime.fromISO(params.data.to_fulfill_at).startOf('day').toJSDate() : null
      },
      width: 180
    },
    {
      headerName: 'Type',
      field: 'delivery_method',
      width: 120,
      filter: true,
      cellRenderer: DeliveryCell
    },
    {
      headerName: 'Location',
      field: 'fulfillment_location_name',
      mobileWidth: 120,
      flex: 1
    },
    {
      headerName: 'Total',
      field: 'total',
      cellRenderer: DollarCell
    },
    {
      headerName: 'Item Count',
      field: 'item_count',
      width: 120
    },
    {
      headerName: 'Discount',
      field: 'total_discount',
      width: 120,
      cellRenderer: DollarCell
    },
    {
      headerName: 'Employee?',
      field: 'customer.is_employee',
      cellRenderer: CheckmarkCell,
      width: 120
    },
    {
      /**
       * @typedef {Object} RowData
       * @property {string} fulfillment_status
       * @property {boolean} is_locked
       */

      /**
       * @typedef {Object} Params
       * @property {RowData} data
       */
      headerName: 'Bagged?',
      field: 'fulfillment_status',
      filter: true,
      cellRenderer: FulfillmentCell,
      pinned: 'left',
      cellClass: (params) => {
        return {
          [FULFILLMENT_STATUS.notBagged]: 'bg-blue-200 text-blue-400',
          [FULFILLMENT_STATUS.bagged]: 'bg-green-200 text-green-400',
          [FULFILLMENT_STATUS.cancelled]: 'bg-red-200 text-red-400',
          [FULFILLMENT_STATUS.changed]: 'bg-yellow-200 text-yellow-400',
          [FULFILLMENT_STATUS.partial]: 'bg-blue-400 text-blue-200'
        }[params.data.fulfillment_status]
      },
      // @Note: This is acting as a hash to determine if the cell should re-render.
      valueGetter: (params) => {
        return `${params.data.fulfillment_status}-${params.data.priority}-${params.data.is_locked.toString()}-${
          params.data.delivery_method
        }`
      },
      filterValueGetter: (params) => {
        return params.data.fulfillment_status
      },
      cellRendererParams: (params) => ({
        status: params.data.fulfillment_status,
        isLocked: params.data.is_locked,
        onClick: () => router.visit(route('fulfillment.show', { id: params.data.id }))
      }),
      width: 210,
      mobileWidth: 130
    }
  ],

  defaultColDef: {
    floatingFilter: true,
    suppressHeaderMenuButton: true
  },

  suppressMovableColumns: true,
  pagination: false,
  animateRows: false,
  sideBar: null,
  rowSelection: 'multiple'
})

useMobileWidths({ gridApi, gridOptions })
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-out;
}
</style>
